/**
 * @file ShopPickupPointsPage.tsx
 * @description Раздел «Доставка → Пункты выдачи» (phase10.5.Y).
 *
 * phase19.12 (запрос пользователя 2026-08-13): фильтры по населённому пункту и
 * перевозчику плюс два вида отображения — списком и на карте.
 *
 * После реальной синхронизации в кэше тысячи НП, поэтому фильтр по населённому
 * пункту — строка поиска с debounce 400 мс, уходящая в серверный параметр `city`
 * (правка по замечанию пользователя 2026-08-14: раньше был select со списком
 * всех городов). Второй инпут фильтрует уже загруженную выдачу по адресу/коду.
 *
 * Карта — Яндекс.Карты по ключу из настроек перевозчика (PickupPointsMap).
 */

import { Loader2, MapPin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  PickupPointBadges,
  PickupPointDetails,
} from "../../../components/carriers/PickupPointDetails";
import { PickupPointsMap } from "../../../components/carriers/PickupPointsMap";
import {
  ALL_CARRIERS,
  PickupPointsFilters,
  type PickupPointsFilterState,
} from "../../../components/carriers/PickupPointsFilters";
import { Alert } from "../../../components/ui/alert";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { ApiError } from "../../../lib/api-client";
import {
  listCarrierPickupPoints,
  listShopCarriers,
  type CarrierKind,
  type ShopPickupPointDto,
} from "../../../lib/api-shop-carriers";

const DEFAULT_CARRIER = "SDEK" as const;
/** Сервер отдаёт максимум столько точек за запрос (take: 200). */
const SERVER_LIMIT = 200;

const INITIAL_FILTERS: PickupPointsFilterState = {
  query: "",
  city: "",
  carrier: ALL_CARRIERS,
  cashOnly: false,
  view: "list",
};

export function ShopPickupPointsPage() {
  const [items, setItems] = useState<ShopPickupPointDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PickupPointsFilterState>(INITIAL_FILTERS);
  const [cityTerm, setCityTerm] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapKey, setMapKey] = useState<string | null>(null);

  const { query, city, carrier: carrierFilter, cashOnly, view } = filters;

  // Debounce 400 мс: строку НП отправляем на сервер, а не фильтруем локально.
  useEffect(() => {
    const timer = setTimeout(() => setCityTerm(city.trim()), 400);
    return () => clearTimeout(timer);
  }, [city]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listCarrierPickupPoints(DEFAULT_CARRIER, {
          city: cityTerm || undefined,
        });
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cityTerm]);

  // Ключ карты берём из настроек перевозчика — отдельного места для него нет.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const carriers = await listShopCarriers();
        const sdek = carriers.find((c) => c.carrier === DEFAULT_CARRIER);
        if (!cancelled) setMapKey(sdek?.yandexMapsApiKey ?? null);
      } catch {
        // Ключ не критичен для списка — молча оставляем карту недоступной.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const carriers = useMemo(
    () => Array.from(new Set(items.map((p) => p.carrier))) as CarrierKind[],
    [items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      if (carrierFilter !== ALL_CARRIERS && p.carrier !== carrierFilter) return false;
      if (cashOnly && !p.hasCashPay) return false;
      if (!q) return true;
      return (
        p.address.toLowerCase().includes(q) || p.externalCode.toLowerCase().includes(q)
      );
    });
  }, [items, query, carrierFilter, cashOnly]);

  const byCity = useMemo(() => {
    const map = new Map<string, ShopPickupPointDto[]>();
    for (const p of filtered) {
      const list = map.get(p.city) ?? [];
      list.push(p);
      map.set(p.city, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], "ru"));
  }, [filtered]);

  const selected = filtered.find((p) => p.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Доставка · Пункты выдачи</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">Пункты выдачи</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Локальный кэш ПВЗ. Обновляется в настройках перевозчика кнопкой
          «Синхронизировать».
        </p>
      </div>

      <EditorialCard>
        <EditorialCard.Body>
          <PickupPointsFilters
            value={filters}
            onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
            carriers={carriers}
            cityLoading={loading || city.trim() !== cityTerm}
            filteredCount={filtered.length}
            loadedCount={items.length}
            truncated={items.length >= SERVER_LIMIT}
            serverLimit={SERVER_LIMIT}
          />
        </EditorialCard.Body>
      </EditorialCard>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" />
        </div>
      ) : filtered.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body className="flex flex-col items-center gap-3 py-12 text-center text-on-surface-variant">
            <MapPin className="size-10 opacity-50" />
            <p>
              {items.length > 0
                ? "Ничего не найдено по выбранным фильтрам."
                : cityTerm
                  ? `Пунктов в населённом пункте «${cityTerm}» не нашлось — проверьте написание.`
                  : "Кэш ПВЗ пуст. Запустите синхронизацию в настройках перевозчика."}
            </p>
          </EditorialCard.Body>
        </EditorialCard>
      ) : view === "map" ? (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <PickupPointsMap
            points={filtered}
            apiKey={mapKey}
            selectedId={selectedId}
            onSelect={(p) => setSelectedId(p.id)}
          />
          <EditorialCard>
            <EditorialCard.Body>
              {selected ? (
                <PickupPointDetails point={selected} />
              ) : (
                <p className="text-sm text-on-surface-variant">
                  Выберите точку на карте, чтобы увидеть подробности.
                </p>
              )}
            </EditorialCard.Body>
          </EditorialCard>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {byCity.map(([cityName, points]) => (
            <EditorialCard key={cityName}>
              <EditorialCard.Header title={cityName} subtitle={`${points.length} ПВЗ`} />
              <EditorialCard.Body className="!p-0">
                <ul className="divide-y divide-on-surface/8">
                  {points.map((p) => (
                    <li key={p.id} className="flex items-start gap-3 px-4 py-3">
                      <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <PickupPointBadges point={p} />
                        <div className="text-sm text-on-surface">{p.address}</div>
                        {p.workingHours ? (
                          <div className="mt-0.5 text-xs text-on-surface-variant">
                            {p.workingHours}
                          </div>
                        ) : null}
                        {/* В списке — только телефон, email раздувал бы строку. */}
                        {p.phone ? (
                          <div className="mt-0.5 text-xs text-on-surface-variant">{p.phone}</div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </EditorialCard.Body>
            </EditorialCard>
          ))}
        </div>
      )}
    </div>
  );
}

